import { useEffect, useState } from "react";

export const BILL_VALIDITY_MS = 5 * 60 * 1000; // QR is valid for 5 minutes (matches expires_at from /store-pay/create)
export const MAX_BILL_AMOUNT = 10000000; // ₹1 crore (same cap as PayBillPanel)
export const BILL_SCAN_PATH = "/web/store-bill"; // customer scan page route

const round2 = (n) => Math.round(n * 100) / 100;

// 8-char reference, e.g. "K7Q2XM9B"
export const makeRef = (length = 8) => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const bytes = new Uint32Array(length);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => chars[b % chars.length]).join("");
};

// "2026-10-24T06:51:09.000000Z" -> ms. Microseconds are trimmed because Safari can choke on them.
const toMs = (iso) => (iso ? Date.parse(String(iso).replace(/(\.\d{3})\d+/, "$1")) : NaN);

/** Store side: build the bill object from the /store-pay/create response ({ payment, qr }) */
export const billFromResponse = ({ payment, qr }) => {
    const createdAt = Number(qr.tm) || Date.now();
    const expiresAt = toMs(payment?.expires_at);
    return {
        id: payment?.id ?? null,
        ref: qr.ref, // server-issued ref - this is what goes in the QR and what status/initiate use
        storeId: String(qr.sid),
        amount: round2(Number(qr.amt)),
        invoice: "",
        phone: "",
        createdAt,
        expiresAt: Number.isFinite(expiresAt) ? expiresAt : createdAt + BILL_VALIDITY_MS,
    };
};

/** Map whatever /store-pay/status returns to: pending | initiated | paid | cancelled | expired */
export const normalizeBillStatus = (res) => {
    const raw = [res?.payment?.status, res?.data?.payment?.status, res?.data?.status, res?.status].find(
        (v) => typeof v === "string"
    );
    const v = String(raw || "").toLowerCase();
    if (["paid", "success", "successful", "completed", "complete"].includes(v)) return "paid";
    if (["initiated", "processing", "in_progress", "in-progress"].includes(v)) return "initiated";
    if (["cancelled", "canceled", "failed", "rejected"].includes(v)) return "cancelled";
    if (v === "expired") return "expired";
    return "pending";
};

/**
 * Store side: the URL that goes inside the QR.
 * s = store id, a = amount, t = created at (ms), r = bill ref, inv = invoice no (optional), ph = customer phone (optional)
 * These come straight from the `qr` object returned by /store-pay/create ({ sid, amt, tm, ref }).
 */
export const buildBillLink = (bill) => {
    const p = new URLSearchParams({
        s: bill.storeId,
        a: String(bill.amount),
        t: String(bill.createdAt),
        r: bill.ref,
    });
    if (bill.invoice) p.set("inv", bill.invoice);
    if (bill.phone) p.set("ph", bill.phone);
    return `${window.location.origin}${BILL_SCAN_PATH}?${p.toString()}`;
};

/** Customer side: read the bill back from the scanned URL. Returns null if the link is invalid. */
export const parseBillParams = (searchParams) => {
    const storeId = searchParams.get("s");
    const amount = round2(parseFloat(searchParams.get("a")));
    const createdAt = Number(searchParams.get("t"));
    const ref = searchParams.get("r");
    if (!storeId || !ref || !createdAt || !(amount > 0) || amount > MAX_BILL_AMOUNT) return null;
    return {
        ref,
        storeId,
        amount,
        invoice: searchParams.get("inv") || "",
        phone: (searchParams.get("ph") || "").replace(/\D/g, "").slice(-10),
        createdAt,
        expiresAt: createdAt + BILL_VALIDITY_MS,
    };
};

/** Milliseconds left until `expiresAt` (0 once expired). Ticks every second. */
export const useCountdown = (expiresAt) => {
    const calc = () => (expiresAt ? Math.max(0, expiresAt - Date.now()) : 0);
    const [left, setLeft] = useState(calc);

    useEffect(() => {
        setLeft(calc());
        if (!expiresAt) return undefined;
        const id = setInterval(() => {
            const next = calc();
            setLeft(next);
            if (next <= 0) clearInterval(id);
        }, 1000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expiresAt]);

    return left;
};

export const formatCountdown = (ms) => {
    const total = Math.ceil(ms / 1000);
    const m = String(Math.floor(total / 60)).padStart(2, "0");
    const s = String(total % 60).padStart(2, "0");
    return `${m}:${s}`;
};